import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, CalendarBlank, Clock, Path, Users, CaretDown } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { VehicleCard } from "./config/VehicleCard";
import { AddressAutocomplete } from "./config/AddressAutocomplete";
import { BookingSummary } from "./config/BookingSummary";
import { VEHICLE_IMAGES, VEHICLE_META } from "../lib/vehicleImages";

const StepLabel = ({ n, children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="cfg-body w-7 h-7 rounded-full bg-[#071B2F] text-[#D4AF37] text-[12px] font-semibold flex items-center justify-center">{n}</span>
    <h4 className="cfg-body text-[13px] font-semibold tracking-widest uppercase text-[#071B2F]">{children}</h4>
  </div>
);

const Counter = ({ label, value, set, min = 0, testid }) => (
  <div className="flex items-center justify-between bg-white border border-[#071B2F]/10 rounded-2xl px-4 py-3">
    <span className="cfg-body text-[14px] text-[#071B2F]/80">{label}</span>
    <div className="flex items-center gap-3">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => set(Math.max(min, value - 1))} className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#D4AF37] hover:text-white text-[#071B2F] flex items-center justify-center transition-colors"><Minus weight="bold" size={14} /></button>
      <span data-testid={testid} className="cfg-body w-6 text-center text-[15px] font-semibold text-[#071B2F]">{value}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => set(value + 1)} className="w-8 h-8 rounded-full bg-[#F4F5F7] hover:bg-[#D4AF37] hover:text-white text-[#071B2F] flex items-center justify-center transition-colors"><Plus weight="bold" size={14} /></button>
    </div>
  </div>
);

const fieldCls = "cfg-body w-full bg-white border border-[#071B2F]/12 rounded-2xl px-4 py-3.5 outline-none text-[#071B2F] placeholder:text-[#071B2F]/35 text-[15px] focus:border-[#D4AF37] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.12)] transition-all";

export const Configurator = ({ embedded = false }) => {
  const { t, lang } = useLang();
  const c = t.config;
  const [vehicle, setVehicle] = useState("business");
  const [service, setService] = useState(c.services[0]);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [lugLarge, setLugLarge] = useState(1);
  const [lugCabin, setLugCabin] = useState(0);
  const [openPax, setOpenPax] = useState(false);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    const apply = (pk, dp) => { if (pk) setPickup({ label: pk }); if (dp) setDropoff({ label: dp }); };
    try {
      const stored = sessionStorage.getItem("prefill-booking");
      if (stored) {
        const { pickup: pk, dropoff: dp } = JSON.parse(stored);
        apply(pk, dp);
        sessionStorage.removeItem("prefill-booking");
      }
    } catch { /* ignore */ }
    const handler = (e) => { const { pickup: pk, dropoff: dp } = e.detail || {}; apply(pk, dp); };
    window.addEventListener("prefill-booking", handler);
    return () => window.removeEventListener("prefill-booking", handler);
  }, []);

  useEffect(() => {
    if (!pickup?.label || !dropoff?.label) { setQuote(null); return; }
    setLoadingQuote(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote", { pickup: pickup.label, dropoff: dropoff.label, vehicle });
        setQuote(data);
      } catch { setQuote(null); }
      finally { setLoadingQuote(false); }
    }, 500);
    return () => clearTimeout(id);
  }, [pickup, dropoff, vehicle]);

  const activeVehicle = c.vehicles.find((v) => v.id === vehicle);
  const pax = `${adults + children}`;

  const book = async () => {
    if (!pickup?.label || !dropoff?.label || !date || !time || !name || !email || !phone) {
      return toast.error(c.errContact);
    }
    setBooking(true);
    try {
      const { data: bk } = await api.post("/bookings", {
        pickup: pickup.label, dropoff: dropoff.label, date, time,
        name, email, phone, passengers: adults + children, lang,
        vehicle, service, adults, children, luggage_large: lugLarge, luggage_cabin: lugCabin,
      });
      const { data: ck } = await api.post("/payments/checkout", { booking_id: bk.id, origin_url: window.location.origin });
      window.location.href = ck.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setBooking(false);
    }
  };

  return (
    <section id="booking" className={`cfg relative w-full bg-white ${embedded ? "pt-6 pb-16 md:pb-24" : "py-16 md:py-24"}`} data-testid="configurator-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        {!embedded && (
        <div className="text-center max-w-2xl mx-auto mb-12">
          <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="cfg-body text-[12px] font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">{c.fleetLabel}</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }} className="cfg-display text-4xl md:text-6xl text-[#071B2F] tracking-tight">{c.title}</motion.h2>
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="cfg-body text-[#071B2F]/55 mt-5 text-base md:text-lg">{c.subtitle}</motion.p>
        </div>
        )}

        {/* Step 1: vehicles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {c.vehicles.map((v, i) => (
            <motion.div key={v.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <VehicleCard v={v} image={VEHICLE_IMAGES[v.id]} meta={VEHICLE_META[v.id]} selected={vehicle === v.id} onSelect={setVehicle} t={c} />
            </motion.div>
          ))}
        </div>

        {/* Steps + summary */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="rounded-[24px] border border-[#071B2F]/10 bg-white shadow-[0_20px_50px_-35px_rgba(7,27,47,0.35)] overflow-hidden divide-y divide-[#071B2F]/8">
              {/* Service */}
              <div className="flex items-center gap-3 px-5 py-4">
                <span className="w-9 h-9 rounded-full bg-[#F4F5F7] flex items-center justify-center shrink-0"><Path weight="fill" size={16} className="text-[#D4AF37]" /></span>
                <span className="cfg-body text-[13px] text-[#071B2F]/60">{c.steps.service}</span>
                <div className="relative ml-auto">
                  <select data-testid="service-select" value={service} onChange={(e) => setService(e.target.value)} className="appearance-none bg-transparent text-right cfg-body text-[14px] font-medium text-[#071B2F] pr-6 outline-none cursor-pointer max-w-[220px]">
                    {c.services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <CaretDown size={13} className="text-[#071B2F]/40 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Passengers & luggage (accordion) */}
              <div>
                <button type="button" onClick={() => setOpenPax((o) => !o)} data-testid="pax-toggle" className="w-full flex items-center gap-3 px-5 py-4 text-left">
                  <span className="w-9 h-9 rounded-full bg-[#F4F5F7] flex items-center justify-center shrink-0"><Users weight="fill" size={16} className="text-[#D4AF37]" /></span>
                  <span className="cfg-body text-[13px] text-[#071B2F]/60">{c.steps.passengers}</span>
                  <span className="ml-auto cfg-body text-[14px] font-medium text-[#071B2F]">{adults + children} {c.pax} · {lugLarge + lugCabin} {c.lug}</span>
                  <CaretDown size={14} className={`text-[#071B2F]/40 transition-transform ${openPax ? "rotate-180" : ""}`} />
                </button>
                {openPax && (
                  <div className="px-5 pb-4">
                    <div className="grid grid-cols-2 gap-2">
                      <Counter label={c.adults} value={adults} set={setAdults} min={1} testid="count-adults" />
                      <Counter label={c.children} value={children} set={setChildren} testid="count-children" />
                      <Counter label={c.luggageLarge} value={lugLarge} set={setLugLarge} testid="count-lug-large" />
                      <Counter label={c.luggageCabin} value={lugCabin} set={setLugCabin} testid="count-lug-cabin" />
                    </div>
                    <button type="button" onClick={() => setOpenPax(false)} data-testid="pax-done" className="font-bookman font-bold w-full mt-3 rounded-2xl bg-[#071B2F] text-white text-[12px] tracking-widest uppercase py-3 hover:bg-[#0a2440] transition-colors">
                      {c.done}
                    </button>
                  </div>
                )}
              </div>

              {/* Pickup */}
              <div className="px-5 py-4">
                <p className="cfg-body text-[10px] tracking-[0.18em] uppercase text-[#071B2F]/40 mb-1.5">{c.steps.pickup}</p>
                <AddressAutocomplete value={pickup?.label} onSelect={setPickup} placeholder={c.pickupPh} showCurrent bare testid="pickup-input" />
              </div>

              {/* Destination */}
              <div className="px-5 py-4">
                <p className="cfg-body text-[10px] tracking-[0.18em] uppercase text-[#071B2F]/40 mb-1.5">{c.steps.destination}</p>
                <AddressAutocomplete value={dropoff?.label} onSelect={setDropoff} placeholder={c.destPh} bare testid="dest-input" />
              </div>

              {/* Date + Time */}
              <div className="flex divide-x divide-[#071B2F]/8">
                <label className="flex-1 flex items-center gap-3 px-5 py-4 cursor-pointer">
                  <CalendarBlank weight="fill" size={16} className="text-[#D4AF37] shrink-0" />
                  <span className="cfg-body text-[13px] text-[#071B2F]/60">{c.steps.date}</span>
                  <input type="date" data-testid="config-date" value={date} onChange={(e) => setDate(e.target.value)} className="ml-auto bg-transparent outline-none cfg-body text-[14px] font-medium text-[#071B2F] text-right cursor-pointer" />
                </label>
                <label className="flex-1 flex items-center gap-3 px-5 py-4 cursor-pointer">
                  <Clock weight="fill" size={16} className="text-[#D4AF37] shrink-0" />
                  <span className="cfg-body text-[13px] text-[#071B2F]/60">{c.steps.time}</span>
                  <input type="time" data-testid="config-time" value={time} onChange={(e) => setTime(e.target.value)} className="ml-auto bg-transparent outline-none cfg-body text-[14px] font-medium text-[#071B2F] text-right cursor-pointer" />
                </label>
              </div>

              {/* Contact */}
              <div className="px-5 py-4">
                <p className="cfg-body text-[10px] tracking-[0.18em] uppercase text-[#071B2F]/40 mb-2">{c.contactTitle}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
                  <input data-testid="config-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={c.name} className="cfg-body bg-transparent border-b border-[#071B2F]/12 py-2 outline-none text-[14px] text-[#071B2F] placeholder:text-[#071B2F]/35 focus:border-[#D4AF37] transition-colors" />
                  <input data-testid="config-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={c.email} className="cfg-body bg-transparent border-b border-[#071B2F]/12 py-2 outline-none text-[14px] text-[#071B2F] placeholder:text-[#071B2F]/35 focus:border-[#D4AF37] transition-colors" />
                  <input data-testid="config-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={c.phone} className="cfg-body bg-transparent border-b border-[#071B2F]/12 py-2 outline-none text-[14px] text-[#071B2F] placeholder:text-[#071B2F]/35 focus:border-[#D4AF37] transition-colors" />
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-5">
            <BookingSummary
              t={c}
              vehicle={activeVehicle}
              image={VEHICLE_IMAGES[vehicle]}
              pax={pax}
              pickup={pickup?.label}
              dropoff={dropoff?.label}
              quote={quote}
              loading={loadingQuote}
              onBook={book}
              booking={booking}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
